/**
 * 几何类 op：右栏「位置与尺寸 / 旋转」直接编辑用。
 *
 * 与拖动一样走世界矩阵 + 夹心乘法；区别只是矩阵来自**数值输入**而非指针。
 * 纯数学部分（geometryWorld / rotationWorld）可单测。
 */
import { pathOf } from '../dom';
import { around, mul, rotate, scale, translate, type Mat, type Rect } from '../geom/matrix';
import type { Command } from '../history';
import { attrPatch, type Patch } from '../patch';
import { applyWorldTransform, type TransformDeps } from './transform';

export interface GeometryTarget {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

const EPS = 1e-9;

/** 目标几何 → 世界矩阵：先绕框原点缩放（改宽高），再平移（改位置） */
export function geometryWorld(box: Rect, target: GeometryTarget): Mat {
  const sx = target.w !== undefined && box.w > EPS ? target.w / box.w : 1;
  const sy = target.h !== undefined && box.h > EPS ? target.h / box.h : 1;
  const dx = target.x !== undefined ? target.x - box.x : 0;
  const dy = target.y !== undefined ? target.y - box.y : 0;
  return mul(translate(dx, dy), around(scale(sx, sy), box.x, box.y));
}

/** 旋转到指定角度：取**最短路径**的角度差（350° → -10° 不该绕一圈） */
export function rotationWorld(box: Rect, currentDeg: number, targetDeg: number): Mat {
  const raw = (Number.isFinite(targetDeg) ? targetDeg : currentDeg) - (Number.isFinite(currentDeg) ? currentDeg : 0);
  const delta = ((((raw + 180) % 360) + 360) % 360) - 180;
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  return around(rotate(delta), cx, cy);
}

/**
 * rect 圆角：只写 `rx`（SVG 规范下 ry 缺省等于 rx），保持最小 diff。
 * 传 0 则移除属性。
 */
export function setCornerRadius(root: Element, els: Iterable<Element>, radius: number): Command {
  const patches: Patch[] = [];
  const r = Math.max(0, radius);
  for (const el of els) {
    if (el.tagName.toLowerCase() !== 'rect') continue;
    const path = pathOf(el, root);
    if (!path) continue;
    const patch = attrPatch(path, 'rx', el.getAttribute('rx'), r > 0 ? String(r) : null);
    if (patch) patches.push(patch);
  }
  return { label: '圆角', patches, mergeKey: 'radius' };
}

export const setGeometry = (
  deps: TransformDeps,
  els: Iterable<Element>,
  box: Rect,
  target: GeometryTarget,
  mergeKey?: string,
): Command => applyWorldTransform(deps, els, geometryWorld(box, target), '几何', mergeKey);

export const setRotation = (
  deps: TransformDeps,
  els: Iterable<Element>,
  box: Rect,
  currentDeg: number,
  targetDeg: number,
): Command => applyWorldTransform(deps, els, rotationWorld(box, currentDeg, targetDeg), '旋转', 'rotate');
